import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import { LoadingComponent } from './loading/loading.component';

export interface ModerationActionDialogData {
  action: 'hide' | 'suspend' | 'pending';
  subject: string;
  isUser: boolean;
  save: () => Promise<unknown>;
}

/** Follows the Bootstrap markup and CDK focus management of the existing BAZAR dialogs. */
@Component({
  standalone: true,
  imports: [TranslatePipe, LoadingComponent],
  styles: `.btn { min-height: 44px; }`,
  template: `
    <div class="modal-content bg-dark text-light border border-primary rounded-3 shadow"
      [class.rtl-mode]="isArabic()" [class.ltr-mode]="!isArabic()">
      <div class="modal-header bg-primary text-black rounded-top-2">
        <h2 class="modal-title fs-5 p-1" id="moderation-action-title">{{ ('MODERATION_' + data.action) | translate }}</h2>
      </div>
      <div class="modal-body m-2" id="moderation-action-message">
        <p>{{ ('MODERATION_CONFIRM_' + data.action) | translate }}</p>
        @if (data.subject) {
          <p class="text-break">
            {{ (data.isUser ? 'MODERATION_REPORTED_AGENT' : 'MODERATION_REPORTED_CONTENT') | translate }}:
            <strong>{{ data.subject }}</strong>
          </p>
        }
        @if (error) { <p class="text-danger" role="alert">{{ error | translate }}</p> }
      </div>
      <div class="modal-footer border-secondary gap-2 m-2" [attr.aria-busy]="saving">
        <button type="button" class="btn btn-outline-light" [disabled]="saving" (click)="dialog.close(false)">
          {{ 'CANCEL' | translate }}
        </button>
        <button type="button" class="btn" [class.btn-danger]="data.action !== 'pending'" [class.btn-primary]="data.action === 'pending'" [disabled]="saving" (click)="save()">
          @if (saving) { <app-loading [height]="24" theme="dark" /> }
          {{ ('MODERATION_CONFIRM_BUTTON_' + data.action) | translate }}
        </button>
      </div>
    </div>
  `,
})
export class ModerationActionDialogComponent {
  readonly data = inject<ModerationActionDialogData>(DIALOG_DATA);
  readonly dialog = inject<DialogRef<boolean>>(DialogRef);
  readonly isArabic = inject(Store).selectSignal(selectIsArabic);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  saving = false;
  error = '';

  async save(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    this.error = '';
    this.dialog.disableClose = true;
    try {
      await this.data.save();
      this.dialog.close(true);
    } catch (error) {
      this.error = error instanceof HttpErrorResponse && error.status === 409 && error.error?.code === 'MODERATION_PENDING_CONFLICT'
        ? 'MODERATION_PENDING_CONFLICT' : error instanceof HttpErrorResponse && error.status === 405
          ? 'MODERATION_ENDPOINT_UNAVAILABLE' : 'MODERATION_FAILED';
      this.toast.error(this.translate.instant(this.error));
    } finally {
      this.saving = false;
      this.dialog.disableClose = false;
    }
  }
}
